import {Color} from '@benchling/nucleus-ui';
import {Box, Button, Space, Text, Group, Tooltip, Stack} from '@mantine/core';
import {captureException} from '@sentry/browser';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import staticURL from 'lib/staticURL';
import {LLMSpinner} from 'llm/loading/LLMLoadingMessage';
import BenchChip from 'ui-library/chips/BenchChip';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {isJqXHR} from 'util/Net';
import {assertNever} from 'util/Types';

import {getChatTypeForNewAgentChat, getAgentTypeIcon, getAgentTypeText} from '../sidepanel/ChatTypeUtil';
import {Card, CardGallery, LoadingCard} from './Cards';
import {getCannedPromptSuggestions, useGetGroupedPromptSuggestions} from './GetPromptSuggestions';
import {PromptSuggestion, PromptSuggestionCategory} from './PromptSuggestionTypes';
import runPromptSuggestion from './runPromptSuggestion';

function getIconData(suggestion: PromptSuggestion): {name: BenchlingIconName | null; text: string} | null {
  switch (suggestion.action.type) {
    case 'NEW_CHAT':
      return {
        name: getAgentTypeIcon(suggestion.action.agentType),
        text: getAgentTypeText(suggestion.action.agentType),
      };
    case 'NEW_CHAT_PLACEHOLDER':
      switch (suggestion.action.agentType) {
        case 'PREDICT':
          return {name: 'crystal-ball', text: 'Predict'};
        default:
          assertNever(suggestion.action);
          return null;
      }
    case 'MODEL_HUB_OPEN_3D_STRUCTURE':
      return {name: 'crystal-ball', text: 'Structure Prediction'};
    default:
      assertNever(suggestion.action);
      return null;
  }
}

function getIcon(suggestion: PromptSuggestion): JSX.Element | null {
  const iconData = getIconData(suggestion);
  return iconData && iconData.name ? (
    <Tooltip label={iconData.text} disabled={!iconData.text}>
      <Box>
        <BenchlingIcon icon={iconData.name} />
      </Box>
    </Tooltip>
  ) : null;
}

/**
 * Summarize the inputs for a suggestion.
 */
function getInputDetails(suggestion: PromptSuggestion): React.ReactNode {
  const input = suggestion.action.input;
  switch (input.type) {
    case 'benchling-objects':
      return (
        <ChipJail>
          {input.ids.map((id) => (
            <div className={css(styles.ellipsizedChipWrapper)} key={id}>
              <BenchChip id={id} size={ChipSize.TABLE_CELL} />
            </div>
          ))}
        </ChipJail>
      );
    case 'demo-file': {
      const path = `/static/llm/example-data/${input.filename}`;
      return (
        <ChipJail>
          <Chip text={input.name} icon="paperclip" size={ChipSize.TABLE_CELL} href={staticURL(path)} />
        </ChipJail>
      );
    }
    case 'none':
      return null;
    default:
      throw new Error(`Unknown input type: ${assertNever(input).type}`);
  }
}

/**
 * Wrapper around a Chip component.
 *
 * A "jail" in two senses:
 * - Forces Chip to render on a single line (and ellipsize if necessary)
 * - Prevents clicks from bubbling, allowing clicking on the chip to open in the workspace vs clicking
 *   outside the chip to trigger the PromptSuggestion.
 */
function ChipJail(props: {children: React.ReactNode}): JSX.Element {
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
      }}
      className={css(styles.chipJail)}
    >
      {props.children}
    </div>
  );
}

/**
 * A card that when clicked will run the suggested action.
 */
export function PromptSuggestionCard(props: {suggestion: PromptSuggestion; compact?: boolean}): JSX.Element {
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const handleClick = useCallback(async () => {
    setSubmitting(true);
    const metadata = {
      suggestionId: props.suggestion.id,
      type: props.suggestion.action.type,
      agentType:
        props.suggestion.action.type === 'NEW_CHAT' || props.suggestion.action.type === 'NEW_CHAT_PLACEHOLDER'
          ? props.suggestion.action.agentType
          : null,
      chatType:
        props.suggestion.action.type === 'NEW_CHAT'
          ? getChatTypeForNewAgentChat(props.suggestion.action.agentType)
          : null,
    };
    // Not all suggestions create chats, so we track the attempt for all suggestions.
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_SUGGESTED_ACTION, metadata);
    let chatId: string | null = null;
    try {
      chatId = await runPromptSuggestion(props.suggestion, setProgress);
    } catch (error) {
      // Explicitly capture the error: in theory this could be 4XX, which normally is not sent to Sentry;
      // however, in this code path, a 4xx would be unexpected. Also, this is an opportunity to inject
      // relevant metadata to help debug the issue. 401s are always plausible (either from signed out user on
      // LLM_ENABLE_PUBLIC_AI_TRIAL_TENANT, or if the user's session expired), so skip capturing them.
      if (!(isJqXHR(error) && error.status === 401)) {
        captureException(error, {tags: {'llm-prompt-suggestion-id': props.suggestion.id}, extra: {metadata}});
      }
      throw error;
    } finally {
      setSubmitting(false);
    }
    // If the suggestion created a chat, capture the success. Include chatId to potentially correlate with
    // user feedback
    if (chatId) {
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CREATED_LLM_CHAT_FROM_SUGGESTED_ACTION, {
        ...metadata,
        chatId,
      });
    }
  }, [props.suggestion]);

  return (
    <Card
      icon={submitting ? <LLMSpinner /> : getIcon(props.suggestion)}
      onClick={handleClick}
      submitting={submitting}
      tabIndex={0}
    >
      <Text>{props.suggestion.summary}</Text>
      {!props.compact && (
        <>
          <Space h="xs" />
          <Text
            // Height of a TABLE_CELL chip, in case there are no input details
            mih="20px"
          >
            {submitting && progress ? (
              <Text fs="italic" color={Color.GRAY_500}>
                {progress}
              </Text>
            ) : (
              getInputDetails(props.suggestion)
            )}
          </Text>
        </>
      )}
    </Card>
  );
}

/**
 * A single card that cycles through multiple suggestions for the same use case.
 */
function CyclingSuggestionCard(props: {suggestions: Array<PromptSuggestion | null>}): JSX.Element {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const validSuggestions = props.suggestions.filter((s): s is PromptSuggestion => s != null);

  useEffect(() => {
    if (validSuggestions.length <= 1) {
      return;
    }

    const interval = setInterval(() => {
      // Don't cycle while hovering or focused to prevent the suggestion from changing
      if (isHovering || isFocused) {
        return;
      }
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % validSuggestions.length);
        setIsTransitioning(false);
      }, 300); // Start fade out, then swap content mid-transition
    }, 6000); // Change suggestion every 6 seconds

    return () => {
      clearInterval(interval);
    };
  }, [validSuggestions.length, isHovering, isFocused]);

  // If there are any loading suggestions, show loading card
  if (props.suggestions.some((s) => s == null)) {
    return <LoadingCard />;
  }

  if (validSuggestions.length === 0) {
    return <LoadingCard />;
  }

  const currentSuggestion = validSuggestions[currentIndex];
  if (!currentSuggestion) {
    return <LoadingCard />;
  }

  return (
    <Stack
      onMouseEnter={() => {
        setIsHovering(true);
      }}
      onMouseLeave={() => {
        setIsHovering(false);
      }}
      onFocus={() => {
        setIsFocused(true);
      }}
      onBlur={() => {
        setIsFocused(false);
      }}
      p={0}
      spacing="sm"
      style={{
        opacity: isTransitioning ? 0 : 1,
        transition: 'opacity 0.5s ease-in-out',
      }}
    >
      <PromptSuggestionCard suggestion={currentSuggestion} />
    </Stack>
  );
}

/**
 * Render a collection of prompt suggestions as vertical cards.
 *
 * Suggestions come from {@link useGetGroupedPromptSuggestions}.
 * Each group of suggestions is rendered as a single card that cycles through the options.
 */
export function PromptSuggestionCardGallery(): JSX.Element {
  const suggestionGroups = useGetGroupedPromptSuggestions();

  return (
    <CardGallery title="Suggested actions">
      {suggestionGroups.length === 0 && <Text className="u-faded">No suggestions currently available.</Text>}
      {suggestionGroups.map((group, index) => (
        <CyclingSuggestionCard key={`group-${index}`} suggestions={group} />
      ))}
    </CardGallery>
  );
}

/**
 * Helper to ensure that switching between prompt suggestion categories does not cause a vertical shift.
 *
 * Vertical shift is the result of vertical centering + having a different number of items in each category..
 *
 * TODO: This is imperfect since it does not account for wrapping of card contents, which will happen on
 * mobile. We may want to revisit this affecting the vertical centering somehow.
 */
function useCardGalleryFiller(suggestions: Array<PromptSuggestion>): JSX.Element {
  return useMemo(() => {
    const maxItems = Math.max(
      ...Object.values(PromptSuggestionCategory).map(
        (category) => getCannedPromptSuggestions(category).length
      )
    );
    const rv = [];
    for (let i = 0; i < maxItems - suggestions.length; i++) {
      rv.push(
        <div style={{opacity: 0}} key={i}>
          <Card icon={null}>
            <Text />
          </Card>
        </div>
      );
    }
    return <>{rv}</>;
  }, [suggestions]);
}

export function SignedOutPromptSuggestionCardGallery(): JSX.Element {
  const [section, setSection] = useState<PromptSuggestionCategory>(PromptSuggestionCategory.FIND_DATA);
  const suggestions = getCannedPromptSuggestions(section);
  const fillerElement = useCardGalleryFiller(suggestions);
  return (
    <CardGallery title="Suggested actions">
      <Group spacing="sm" position="left">
        {Object.values(PromptSuggestionCategory).map((aSection) => (
          <Button
            style={{
              // Full-width, but "brick" layout on narrow layout.
              flex: '1 0 auto',
              minWidth: 'max-content',
              // Set max-width to avoid awkward full-width button.
              maxWidth: '50%',
            }}
            // eslint-disable-next-line no-restricted-syntax
            variant={section === aSection ? 'filled' : 'light'}
            color="grey"
            key={aSection}
            onClick={() => {
              setSection(aSection);
            }}
          >
            {aSection}
          </Button>
        ))}
      </Group>
      {suggestions.map((suggestion) => (
        <PromptSuggestionCard compact={true} key={suggestion.id} suggestion={suggestion} />
      ))}
      {fillerElement}
    </CardGallery>
  );
}

const styles = StyleSheet.create({
  chipJail: {
    // chips will ellipsize if there are not allowed to wrap.
    whiteSpace: 'nowrap',
    // Tightly hug the chip component to avoid the jail from swallowing clicks outside the chip.
    display: 'inline-flex',
    maxWidth: '100%',
    gap: '6px',
  },
  ellipsizedChipWrapper: {
    flex: 1,
    minWidth: 0,
    maxWidth: 'fit-content',
  },
});
